// API utility functions
// Определяем базовый URL API автоматически на основе текущего хоста
const getApiBaseUrl = () => {
  // Используем переменную окружения, если она задана (для production)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // В режиме разработки используем текущий хост, но порт 8000 для бэкенда
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://127.0.0.1:8000/api';
  }
  // Если доступ из локальной сети, используем тот же хост, но порт 8000
  const hostname = window.location.hostname;
  return `http://${hostname}:8000/api`;
};

const API_BASE_URL = getApiBaseUrl();

// Callback для обработки истечения сессии
let onSessionExpired = null;

export const setSessionExpiredHandler = (handler) => {
  onSessionExpired = handler;
};

export const getAuthHeaders = (includeContentType = true) => {
  const token = localStorage.getItem('access_token');
  const headers = {
    'Authorization': `Bearer ${token}`,
  };
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

// Функция для обработки ответов API с проверкой на 401
const handleApiResponse = async (response) => {
  if (response.status === 401) {
    // Сессия истекла
    if (onSessionExpired) {
      onSessionExpired();
    }
    throw new Error('SESSION_EXPIRED');
  }
  return response;
};

// Изменение полезных ссылок: ошибки валидации отдаём как JSON-строку,
// страница разбирает её в понятный текст
const linksRequest = async (path, method, data) => {
  const response = await fetch(`${API_BASE_URL}/${path}`, {
    method,
    headers: getAuthHeaders(),
    body: data === undefined ? undefined : JSON.stringify(data),
  });
  await handleApiResponse(response);
  if (!response.ok) {
    let error = {};
    try {
      error = await response.json();
    } catch (e) {
      // тело ответа не JSON
    }
    throw new Error(error.detail || JSON.stringify(error) || 'Failed to save link');
  }
  return method === 'DELETE' ? true : response.json();
};

export const api = {
  // Authentication
  login: async (username, password) => {
    const response = await fetch(`${API_BASE_URL}/token/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });
    if (!response.ok) {
      let error = {};
      try {
        const text = await response.text();
        error = text ? JSON.parse(text) : {};
        // Логируем полный ответ для отладки
        console.error('Login error response:', {
          status: response.status,
          statusText: response.statusText,
          body: error,
          rawText: text
        });
      } catch (e) {
        console.error('Failed to parse error response:', e);
      }
      
      if (response.status === 401) {
        throw new Error('Неверный логин или пароль');
      }
      // Для 400 ошибок показываем детали
      if (response.status === 400) {
        // Пробуем извлечь понятное сообщение об ошибке
        let errorMsg = 'Ошибка входа. Проверьте правильность данных';
        if (Array.isArray(error.username)) {
          errorMsg = error.username[0];
        } else if (Array.isArray(error.password)) {
          errorMsg = error.password[0];
        } else if (Array.isArray(error.non_field_errors)) {
          errorMsg = error.non_field_errors[0];
        } else if (error.username) {
          errorMsg = error.username;
        } else if (error.password) {
          errorMsg = error.password;
        } else if (error.detail) {
          errorMsg = error.detail;
        } else if (error.non_field_errors) {
          errorMsg = error.non_field_errors;
        }
        throw new Error(errorMsg);
      }
      throw new Error(error.detail || error.error || 'Ошибка входа');
    }
    return response.json();
  },

  // Предпросмотр офисного документа: сервер отдаёт его в виде PDF.
  // Запрос идёт с токеном, поэтому забираем файл через fetch,
  // а не подставляем ссылку в iframe напрямую.
  getAttachmentPreview: async (attachmentId) => {
    const response = await fetch(`${API_BASE_URL}/attachments/${attachmentId}/preview/`, {
      headers: getAuthHeaders(false),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      let detail = 'Не удалось открыть предпросмотр';
      try {
        const error = await response.json();
        if (error && error.detail) detail = error.detail;
      } catch (e) {
        /* тело может быть не JSON — оставляем общий текст */
      }
      throw new Error(detail);
    }
    return response.blob();
  },

  // Основное расписание из МЭШ (уроки) — страница «Расписание» и блок «Сейчас»
  // Карточка «Приложение в RuStore» (/mobile-app/: адреса и классы со словом
  // «banner» режут блокировщики рекламы). На странице входа запрашиваем без токена:
  // устаревший токен вызвал бы 401 и окно «Сессия истекла».
  getAppBanner: async ({ withAuth = false } = {}) => {
    const token = withAuth && localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/mobile-app/`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Не удалось загрузить баннер');
    return response.json();
  },

  updateAppBanner: async (data) => {
    const response = await fetch(`${API_BASE_URL}/mobile-app/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    await handleApiResponse(response);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const first = Object.values(body).flat()[0];
      throw new Error(first || 'Не удалось сохранить баннер');
    }
    return body;
  },

  getTimetable: async () => {
    const response = await fetch(`${API_BASE_URL}/timetable/`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch timetable');
    return response.json();
  },

  updateTimetableSettings: async (data) => {
    const response = await fetch(`${API_BASE_URL}/timetable/settings/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const first = (field) => error[field] && error[field][0];
      throw new Error(first('lesson_minutes') || first('valid_until') || 'Не удалось сохранить');
    }
    return response.json();
  },

  // Полезные ссылки: блоки приходят вместе со ссылками
  getLinkGroups: async () => {
    const response = await fetch(`${API_BASE_URL}/link-groups/`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch links');
    return response.json();
  },

  createLinkGroup: async (data) => linksRequest('link-groups/', 'POST', data),
  updateLinkGroup: async (id, data) => linksRequest(`link-groups/${id}/`, 'PATCH', data),
  deleteLinkGroup: async (id) => linksRequest(`link-groups/${id}/`, 'DELETE'),
  createLink: async (data) => linksRequest('links/', 'POST', data),
  updateLink: async (id, data) => linksRequest(`links/${id}/`, 'PATCH', data),
  deleteLink: async (id) => linksRequest(`links/${id}/`, 'DELETE'),

  // Кабинеты (распределение по классам)
  getClassrooms: async () => {
    const response = await fetch(`${API_BASE_URL}/classrooms/`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch classrooms');
    return response.json();
  },

  createClassroom: async (data) => {
    const response = await fetch(`${API_BASE_URL}/classrooms/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to create classroom');
    }
    return response.json();
  },

  updateClassroom: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/classrooms/${id}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to update classroom');
    }
    return response.json();
  },

  deleteClassroom: async (id) => {
    const response = await fetch(`${API_BASE_URL}/classrooms/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to delete classroom');
    return true;
  },

  getClassroomSettings: async () => {
    const response = await fetch(`${API_BASE_URL}/classrooms-settings/`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch classroom settings');
    return response.json();
  },

  updateClassroomSettings: async (data) => {
    const response = await fetch(`${API_BASE_URL}/classrooms-settings/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to update settings');
    }
    return response.json();
  },

  // Sections
  getSections: async () => {
    const response = await fetch(`${API_BASE_URL}/sections/`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch sections');
    return response.json();
  },

  // Posts
  getPosts: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/posts/${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch posts');
    return response.json();
  },

  getPost: async (id) => {
    const response = await fetch(`${API_BASE_URL}/posts/${id}/`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch post');
    return response.json();
  },

  // Subcategories
  getSubcategories: async () => {
    const response = await fetch(`${API_BASE_URL}/subcategories/`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch subcategories');
    return response.json();
  },

  // User
  getCurrentUser: async () => {
    const response = await fetch(`${API_BASE_URL}/user/me/`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch user');
    return response.json();
  },

  updateCurrentUser: async (data) => {
    const formData = new FormData();
    
    // Добавляем текстовые поля
    if (data.username) formData.append('username', data.username);
    if (data.email) formData.append('email', data.email);
    if (data.first_name) formData.append('first_name', data.first_name);
    if (data.last_name) formData.append('last_name', data.last_name);
    if (data.middle_name !== undefined) formData.append('middle_name', data.middle_name || '');
    
    // Добавляем файл аватара, если есть
    if (data.avatar && data.avatar instanceof File) {
      formData.append('avatar', data.avatar);
    }
    
    const response = await fetch(`${API_BASE_URL}/user/me/`, {
      method: 'PATCH',
      headers: getAuthHeaders(false), // Не добавляем Content-Type для FormData
      body: formData,
    });
    
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.message || 'Failed to update user');
    }
    return response.json();
  },

  // Admin API
  // Users
  getUsers: async () => {
    const response = await fetch(`${API_BASE_URL}/users/`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch users');
    return response.json();
  },

  createUser: async (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        if (key === 'avatar' && data[key] instanceof File) {
          formData.append(key, data[key]);
        } else {
          formData.append(key, data[key]);
        }
      }
    });

    const response = await fetch(`${API_BASE_URL}/users/`, {
      method: 'POST',
      headers: getAuthHeaders(false),
      body: formData,
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to create user');
    }
    return response.json();
  },

  updateUser: async (id, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        if (key === 'avatar' && data[key] instanceof File) {
          formData.append(key, data[key]);
        } else {
          formData.append(key, data[key]);
        }
      }
    });

    const response = await fetch(`${API_BASE_URL}/users/${id}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(false),
      body: formData,
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to update user');
    }
    return response.json();
  },

  deleteUser: async (id) => {
    const response = await fetch(`${API_BASE_URL}/users/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to delete user');
    return true;
  },

  // Sections
  createSection: async (data) => {
    const response = await fetch(`${API_BASE_URL}/sections/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to create section');
    }
    return response.json();
  },

  updateSection: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/sections/${id}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to update section');
    }
    return response.json();
  },

  deleteSection: async (id) => {
    const response = await fetch(`${API_BASE_URL}/sections/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to delete section');
    return true;
  },

  // Subcategories
  createSubcategory: async (data) => {
    const response = await fetch(`${API_BASE_URL}/subcategories/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to create subcategory');
    }
    return response.json();
  },

  updateSubcategory: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/subcategories/${id}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to update subcategory');
    }
    return response.json();
  },

  deleteSubcategory: async (id) => {
    const response = await fetch(`${API_BASE_URL}/subcategories/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to delete subcategory');
    return true;
  },

  // Posts
  createPost: async (data) => {
    // Если data - это FormData, не добавляем Content-Type
    const isFormData = data instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/posts/`, {
      method: 'POST',
      headers: isFormData ? getAuthHeaders(false) : getAuthHeaders(),
      body: isFormData ? data : JSON.stringify(data),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to create post');
    }
    return response.json();
  },

  updatePost: async (id, data) => {
    // Если data - это FormData, не добавляем Content-Type
    const isFormData = data instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/posts/${id}/`, {
      method: 'PATCH',
      headers: isFormData ? getAuthHeaders(false) : getAuthHeaders(),
      body: isFormData ? data : JSON.stringify(data),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to update post');
    }
    return response.json();
  },

  deletePost: async (id) => {
    const response = await fetch(`${API_BASE_URL}/posts/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.detail || 'Failed to delete post');
    }
    return true;
  },

  restorePost: async (postId) => {
    const response = await fetch(`${API_BASE_URL}/posts/${postId}/restore/`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to restore post');
    return response.json();
  },

  permanentDeletePost: async (postId) => {
    const response = await fetch(`${API_BASE_URL}/posts/${postId}/permanent-delete/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to permanently delete post');
    return true;
  },

  getTrashPosts: async () => {
    const response = await fetch(`${API_BASE_URL}/posts/?trash=true`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch trash posts');
    return response.json();
  },

  // File Manager
  getFiles: async () => {
    const response = await fetch(`${API_BASE_URL}/files/`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch files');
    return response.json();
  },

  deleteFile: async (id) => {
    const response = await fetch(`${API_BASE_URL}/files/${id}/delete/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to delete file');
    return true;
  },

  replaceFile: async (id, file) => {
    if (!file) {
      throw new Error('File is required');
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    console.log('Sending file replace request:', id, file.name);
    
    const response = await fetch(`${API_BASE_URL}/files/${id}/replace/`, {
      method: 'POST',
      headers: getAuthHeaders(false),
      body: formData,
    });
    
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('Replace file error:', error);
      throw new Error(error.error || error.detail || 'Failed to replace file');
    }
    
    const result = await response.json();
    console.log('File replaced successfully:', result);
    return result;
  },

  renameFile: async (id, newName) => {
    if (!newName || !newName.trim()) {
      throw new Error('New file name is required');
    }
    
    const response = await fetch(`${API_BASE_URL}/files/${id}/rename/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ file_name: newName.trim() }),
    });
    
    await handleApiResponse(response);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('Rename file error:', error);
      throw new Error(error.error || error.detail || 'Failed to rename file');
    }
    
    const result = await response.json();
    console.log('File renamed successfully:', result);
    return result;
  },

  getDiskSpace: async () => {
    const response = await fetch(`${API_BASE_URL}/files/disk-space/`, {
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);
    if (!response.ok) throw new Error('Failed to fetch disk space');
    return response.json();
  },

  // Schedule
  getScheduleClasses: async () => {
    const response = await fetch(`${API_BASE_URL}/schedule/classes/`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.detail || `Ошибка ${response.status}: Не удалось загрузить список классов`);
    }
    return response.json();
  },

  getSchedule: async (class_name) => {
    const queryString = class_name ? `?class_name=${encodeURIComponent(class_name)}` : '';
    const response = await fetch(`${API_BASE_URL}/schedule/${queryString}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch schedule');
    return response.json();
  },

  getAllSchedules: async () => {
    const response = await fetch(`${API_BASE_URL}/schedule/`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch all schedules');
    return response.json();
  },

  deleteSchedule: async (id) => {
    const response = await fetch(`${API_BASE_URL}/schedule/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete schedule');
    return true;
  },

  uploadSchedule: async (file, scheduleType = 'main', clearExisting = false, validUntil = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('schedule_type', scheduleType);
    formData.append('clear_existing', clearExisting ? 'true' : 'false');
    // До какой даты основное расписание актуально (ГГГГ-ММ-ДД)
    if (validUntil) formData.append('valid_until', validUntil);
    const response = await fetch(`${API_BASE_URL}/schedule/upload/`, {
      method: 'POST',
      headers: getAuthHeaders(false),
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload schedule');
    }
    return response.json();
  },

  getScheduleSheets: async (scheduleType = 'main') => {
    const queryString = `?schedule_type=${scheduleType}`;
    const response = await fetch(`${API_BASE_URL}/schedule/sheets${queryString}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch schedule sheets');
    }
    return response.json();
  },

  getScheduleSheetData: async (sheetName, scheduleType = 'main') => {
    const queryString = `?sheet_name=${encodeURIComponent(sheetName)}&schedule_type=${scheduleType}`;
    const response = await fetch(`${API_BASE_URL}/schedule/sheet-data${queryString}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch schedule sheet data');
    }
    return response.json();
  },

  // Polls
  votePoll: async (optionId) => {
    const response = await fetch(`${API_BASE_URL}/polls/${optionId}/vote/`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to vote');
    }
    return response.json();
  },

  unvotePoll: async (optionId) => {
    const response = await fetch(`${API_BASE_URL}/polls/${optionId}/unvote/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to unvote');
    }
    return response.json();
  },
};

